import { Box, Chip } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiReq from 'core/api';
import DataTablePage from 'components/DataTablePage';
import { Role, User } from 'types';
import { useSnackbar } from 'components/GlobalSnackbar';
import type { YandexTrackerQueue } from 'model/yandex-tracker';
import { formatUnixTimestamp } from 'utils/dateUtils';

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const showSnackbar = useSnackbar();

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiReq<User[]>('user/all');
      setUsers(data || []);
    } catch (error) {
      showSnackbar({ message: 'Ошибка загрузки пользователей', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = () => {
    navigate('/users/create');
  };

  const handleEditUser = (user: User) => {
    navigate(`/users/edit/${user.id}`);
  };

  const handleDeleteUser = async (user: User) => {
    try {
      await apiReq(`user/delete`, 'DELETE', { id: user.id });
      showSnackbar({ message: 'Пользователь успешно удален', severity: 'success' });
      loadUsers();
    } catch (error) {
      if (error instanceof Error) {
        showSnackbar({ message: error.message, severity: 'error' });
      } else {
        showSnackbar({ message: 'Ошибка удаления пользователя', severity: 'error' });
      }
    }
  };

  const renderRoles = (roles: Role[]) => {
    if (!roles || roles.length === 0) return '-';

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {roles.map((role) => (
          <Chip key={role.id} label={role.title} size="small" variant="outlined" />
        ))}
      </Box>
    );
  };

  const renderQueues = (queues: YandexTrackerQueue[]) => {
    if (!queues || !Array.isArray(queues) || queues.length === 0) return '-';

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {queues.map((queue) => (
          <Chip key={queue.id} label={queue.name} size="small" variant="outlined" />
        ))}
      </Box>
    );
  };

  const columns = [
    {
      key: 'name',
      label: 'Имя',
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'roles',
      label: 'Роли',
      render: (value: unknown) => renderRoles(value as Role[]),
    },
    {
      key: 'yandex_tracker_queues',
      label: 'Очереди',
      render: (value: unknown) => renderQueues(value as YandexTrackerQueue[]),
    },
    {
      key: 'is_admin',
      label: 'Админ',
      render: (value: unknown) => {
        const is_admin = value as boolean;
        return (
          <Chip
            label={is_admin ? 'Да' : 'Нет'}
            color={is_admin ? 'success' : 'default'}
            size="small"
          />
        );
      },
      filterable: true,
      filter: {
        type: 'boolean' as const,
      },
    },
    {
      key: 'is_active',
      label: 'Статус',
      render: (value: unknown) => {
        const is_active = value as boolean;
        return (
          <Chip
            label={is_active ? 'Активен' : 'Неактивен'}
            color={is_active ? 'success' : 'default'}
            size="small"
          />
        );
      },
      filterable: true,
      filter: {
        type: 'select' as const,
        options: [
          { value: true, label: 'Активен' },
          { value: false, label: 'Неактивен' },
        ],
      },
    },
    {
      key: 'created_at',
      label: 'Дата создания',
      render: (value: unknown) => formatUnixTimestamp(value as number | string),
      sortable: true,
      sortFn: (a: unknown, b: unknown) => {
        const aValue = typeof a === 'number' ? a : typeof a === 'string' ? Number(a) : 0;
        const bValue = typeof b === 'number' ? b : typeof b === 'string' ? Number(b) : 0;
        return aValue - bValue;
      },
    },
  ];

  return (
    <>
      <DataTablePage<User>
        title="Пользователи"
        columns={columns}
        data={users}
        isLoading={isLoading}
        onCreate={handleCreateUser}
        onEdit={handleEditUser}
        onDelete={handleDeleteUser}
        createButtonText="Создать пользователя"
        emptyMessage="Пользователи не найдены"
      />
    </>
  );
};

export default UsersPage;
